import {
  HHChip,
  HHDivider,
  HHStack,
} from '@hinge-health/react-component-library';
import {
  BuildCircleOutlined,
  OpenInNew,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import {
  leftPanelAdminPanelAction,
  leftPanelBillsDestination,
  leftPanelClient,
  leftPanelClientInsurerId,
  leftPanelInsurer,
  leftPanelPartner,
  leftPanelToolDestination,
} from '../constants/strings/left-panel-labels';
import { routes } from '../constants/strings/routes';
import { useActiveContractPartnership } from '../hooks/use-active-contract-partnership';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import { ClientInsurerType } from '../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../utils/bills-utils';
import { buildBillManagementUrl } from './bills/bill-management/bill-management-navigator';
import { buildToolUrl } from './tool/build-tool-url';
import {
  RecordPanel,
  RecordPanelDestination,
  RecordPanelDestinations,
  RecordPanelExternalLink,
  RecordPanelField,
  RecordPanelHeading,
  RecordPanelMeta,
  RecordPanelTitle,
} from './utils/record-panel';

export const LEFT_PANEL_TEST_ID = 'billing-tool-left-panel';
export const LEFT_PANEL_VIEW_BILLS_LINK_TEST_ID =
  'billing-tool-left-panel-view-bills-link';
export const LEFT_PANEL_CLIENT_LINK_TEST_ID =
  'billing-tool-left-panel-client-link';
export const LEFT_PANEL_STATUS_CHIP_TEST_ID =
  'billing-tool-left-panel-status-chip';
export const LEFT_PANEL_ADMIN_LINK_TEST_ID =
  'billing-tool-left-panel-admin-link';
export const LEFT_PANEL_TOOL_LINK_TEST_ID = 'billing-tool-left-panel-tool-link';

export interface BillingToolPanelProps {
  data: ClientInsurerType;
  clientInsurerId?: number;
}

const BillingLeftPanel = ({
  data,
  clientInsurerId,
}: BillingToolPanelProps): JSX.Element => {
  const {
    client: { name: clientName, id: clientId },
    insurer: { name: insurerName, id: insurerId },
    isActive,
    partnerships,
  } = data;
  const billingPartnership = partnerships.filter(
    p => p.partnershipType === 'billing',
  )[0];
  const billsUrl = buildBillManagementUrl({
    clientIds: [clientId],
    insurerIds: [insurerId],
  });

  const canWrite = useBillingWriteAccess();
  // The tool runs a submission, so read-only users never see the row — the
  // route itself is behind RequireBillingWrite and would only bounce them.
  const { partnershipId, loading: partnershipLoading } =
    useActiveContractPartnership(clientInsurerId ?? 0);
  const toolUrl = buildToolUrl({ clientId, insurerId, partnershipId });

  const openAdminPanel = (): void => {
    const baseAdminPanelUrl = process.env.REACT_APP_ADMIN_PANEL_URL || '';

    window.open(
      `${baseAdminPanelUrl}/admin/clients_insurers/${clientInsurerId}`,
      '_blank',
    );
  };

  return (
    <RecordPanel testId={LEFT_PANEL_TEST_ID}>
      <RecordPanelHeading label={leftPanelClient} id={`Id ${clientId}`}>
        <RecordPanelTitle
          to={`${routes.billing.home}/${routes.billing.clientPath}/${clientId}`}
          testId={LEFT_PANEL_CLIENT_LINK_TEST_ID}
        >
          {capitalizeFirstLetterAndSpaceSnakeString(clientName)}
        </RecordPanelTitle>
      </RecordPanelHeading>

      <HHStack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        marginTop={2}
      >
        <HHChip
          hhVariant="variant-bypass"
          color="primary"
          size="small"
          disabled={!isActive}
          data-testid={LEFT_PANEL_STATUS_CHIP_TEST_ID}
          label={isActive ? 'Active' : 'Inactive'}
        />
        <RecordPanelMeta
          label={leftPanelClientInsurerId}
          value={clientInsurerId?.toString() ?? ''}
        />
      </HHStack>

      <HHDivider sx={{ marginY: 5 }} />

      <HHStack direction="column" gap={4}>
        <RecordPanelField
          label={leftPanelInsurer}
          id={`Id ${insurerId}`}
          value={insurerName}
        />
        {billingPartnership ? (
          <RecordPanelField
            label={leftPanelPartner}
            id={`Id ${billingPartnership.id}`}
            value={billingPartnership.name}
          />
        ) : null}
      </HHStack>

      <RecordPanelDestinations>
        <RecordPanelDestination
          testId={LEFT_PANEL_VIEW_BILLS_LINK_TEST_ID}
          icon={<ReceiptLongOutlined sx={{ fontSize: 16 }} />}
          href={billsUrl}
        >
          {leftPanelBillsDestination}
        </RecordPanelDestination>

        {canWrite && !partnershipLoading && (
          <RecordPanelDestination
            testId={LEFT_PANEL_TOOL_LINK_TEST_ID}
            icon={<BuildCircleOutlined sx={{ fontSize: 16 }} />}
            href={toolUrl}
          >
            {leftPanelToolDestination}
          </RecordPanelDestination>
        )}
      </RecordPanelDestinations>

      <RecordPanelExternalLink
        testId={LEFT_PANEL_ADMIN_LINK_TEST_ID}
        icon={<OpenInNew sx={{ fontSize: 14 }} />}
        onClick={openAdminPanel}
      >
        {leftPanelAdminPanelAction}
      </RecordPanelExternalLink>
    </RecordPanel>
  );
};

export default BillingLeftPanel;
